/**
 * Firebase Storage resumable video upload with app-level retries for transient
 * network / HTTP2 failures (ERR_HTTP2_PROTOCOL_ERROR on storage.googleapis.com).
 */

import { ref as storageRef, uploadBytesResumable } from 'firebase/storage';

export const VIDEO_UPLOAD_APP_RETRY_MAX = 4;
export const VIDEO_UPLOAD_RETRY_BASE_MS = 2000;

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** @param {unknown} err */
export function isRetryableVideoUploadError(err) {
  if (!err || typeof err !== 'object') return true;
  const code = typeof err.code === 'string' ? err.code : '';
  if (code === 'storage/canceled') return false;
  if (code === 'storage/unauthorized' || code === 'storage/unauthenticated') return false;
  if (code === 'storage/quota-exceeded' || code === 'storage/invalid-checksum') return false;
  const msg = typeof err.message === 'string' ? err.message.toLowerCase() : '';
  if (msg.includes('permission') || msg.includes('unauthorized') || msg.includes('quota')) return false;
  return true;
}

/** @param {unknown} err */
export function formatVideoUploadErrorMessage(err, fallback = 'Upload failed') {
  const code = err && typeof err === 'object' && typeof err.code === 'string' ? err.code : '';
  const msg = err && typeof err === 'object' && typeof err.message === 'string' ? err.message : '';
  if (code === 'storage/canceled') return 'Cancelled by user';
  if (code === 'storage/unauthorized' || code === 'storage/unauthenticated') {
    return 'Upload not authorized — sign in again and retry.';
  }
  if (code === 'storage/quota-exceeded') {
    return 'Storage quota exceeded — free up space or upgrade your plan.';
  }
  const combined = `${code} ${msg}`.toLowerCase();
  if (
    combined.includes('network')
    || combined.includes('http2')
    || combined.includes('protocol')
    || combined.includes('fetch')
    || combined.includes('connection')
    || combined.includes('timeout')
    || combined.includes('retry')
    || combined.includes('offline')
  ) {
    return 'Upload connection lost — check your network and try again.';
  }
  return msg || fallback;
}

/**
 * @param {object} options
 * @param {import('firebase/storage').FirebaseStorage} options.storage
 * @param {string} options.uploadPath
 * @param {Blob | File} options.file
 * @param {string} options.contentType
 * @param {(transferred: number, total: number) => void} options.onByteProgress
 * @param {{ current: import('firebase/storage').UploadTask | null }} options.uploadTaskRef
 * @param {() => boolean} [options.isCancelled]
 * @param {(attempt: number, maxAttempts: number) => void} [options.onRetry]
 */
export async function uploadVideoResumableWithRetry({
  storage,
  uploadPath,
  file,
  contentType,
  onByteProgress,
  uploadTaskRef,
  isCancelled = () => false,
  onRetry,
}) {
  const maxAttempts = VIDEO_UPLOAD_APP_RETRY_MAX + 1;
  let lastError = null;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (isCancelled()) {
      throw Object.assign(new Error('Cancelled by user'), { code: 'storage/canceled' });
    }
    if (attempt > 0) {
      onRetry?.(attempt + 1, maxAttempts);
      await sleep(VIDEO_UPLOAD_RETRY_BASE_MS * (2 ** (attempt - 1)));
    }

    const uploadTask = uploadBytesResumable(storageRef(storage, uploadPath), file, { contentType });
    uploadTaskRef.current = uploadTask;

    try {
      await new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            onByteProgress(snapshot.bytesTransferred, snapshot.totalBytes);
          },
          reject,
          resolve,
        );
      });
      if (uploadTaskRef.current === uploadTask) uploadTaskRef.current = null;
      return;
    } catch (err) {
      if (uploadTaskRef.current === uploadTask) uploadTaskRef.current = null;
      lastError = err;
      if (err?.code === 'storage/canceled' || isCancelled()) throw err;
      if (!isRetryableVideoUploadError(err) || attempt >= maxAttempts - 1) throw err;
    }
  }

  throw lastError ?? new Error('Upload failed');
}
